import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { MessageCircle, XCircle, Loader2 } from "lucide-react";
import { API, useAuth } from "../App";

const maskInput = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const isValidBRMobile = (raw) => {
  const d = (raw || "").replace(/\D/g, "");
  return d.length === 11 && d[2] === "9";
};

/**
 * Inline dialog for editing the employee's own WhatsApp number /
 * declaring "I don't have WhatsApp". Wraps POST /api/employee/whatsapp-setup.
 */
const WhatsAppEditDialog = ({ open, onOpenChange, profile, onChanged }) => {
  const { token } = useAuth();
  const [mode, setMode] = useState("save"); // save | skip
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const current = profile?.whatsapp || profile?.telefone || "";
    const digits = String(current).replace(/\D/g, "");
    const local = digits.startsWith("55") ? digits.slice(2) : digits;
    setValue(maskInput(local));
    setMode(profile?.whatsapp_status === "none" ? "skip" : "save");
  }, [open, profile]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload =
        mode === "skip"
          ? { action: "skip" }
          : {
              action: "save",
              whatsapp: "+55" + value.replace(/\D/g, ""),
            };
      if (mode === "save" && !isValidBRMobile(value)) {
        toast.error("Número inválido. Use (DDD) 9XXXX-XXXX.");
        setSaving(false);
        return;
      }
      const r = await axios.post(`${API}/employee/whatsapp-setup`, payload, {
        params: { token },
        timeout: 10000,
      });
      toast.success("WhatsApp atualizado!");
      onChanged?.(r.data?.whatsapp_status);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="whatsapp-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            Atualizar WhatsApp
          </DialogTitle>
          <DialogDescription className="text-xs">
            Esse número receberá os códigos de verificação e mensagens
            oficiais da empresa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("save")}
              className={`p-2.5 text-xs rounded-md border transition ${
                mode === "save"
                  ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
              data-testid="whatsapp-edit-mode-save"
            >
              <MessageCircle className="w-4 h-4 mx-auto mb-1" />
              Tenho WhatsApp
            </button>
            <button
              type="button"
              onClick={() => setMode("skip")}
              className={`p-2.5 text-xs rounded-md border transition ${
                mode === "skip"
                  ? "border-slate-700 bg-slate-100 text-slate-800"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
              data-testid="whatsapp-edit-mode-skip"
            >
              <XCircle className="w-4 h-4 mx-auto mb-1" />
              Não tenho
            </button>
          </div>

          {mode === "save" && (
            <div>
              <Label htmlFor="wa-input" className="text-xs">
                Número (com DDD)
              </Label>
              <Input
                id="wa-input"
                type="tel"
                inputMode="numeric"
                value={value}
                onChange={(e) => setValue(maskInput(e.target.value))}
                placeholder="(71) 99999-9999"
                className="mt-1.5 h-11 font-mono"
                data-testid="whatsapp-edit-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={saving || (mode === "save" && !isValidBRMobile(value))}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="whatsapp-edit-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppEditDialog;
